<template>
  <div class="category-page">
    <block-buttons
      title="Категории"
      description="Управляйте категориями каталога товаров"
      :actions="toolbarActions"
    >
      <template #icon>
        <Folder :size="22" />
      </template>
    </block-buttons>

    <data-table
      :rows="getData"
      :columns="tableColumns"
      :loading="loading"
      :error="error"
      :search-keys="['title']"
      title="Категории товаров"
      description="Просмотр и редактирование категорий каталога"
      search-placeholder="Название категории"
      empty-text="Категорий пока нет"
      @retry="loadDataWrapper(true)"
    >
      <template #header>
        <span class="category-page__count">Категорий: {{ getData.length }}</span>
      </template>

      <template #cell-title="{ row }">
        <strong>{{ row.title }}</strong>
      </template>

      <template #cell-actions="{ row }">
        <select
          :disabled="loading"
          :aria-label="`Действия с категорией ${row.title }`"
          @change="selectAction($event, row)"
        >
          <option value="">Действия</option>

          <option
            v-for="action in rowActions"
            :key="action.id"
            :value="action.id"
          >
            {{ action.label }}
          </option>
        </select>
      </template>
    </data-table>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import {
  Folder,
  Plus,
  RefreshCw
} from 'lucide-vue'

import BlockButtons from '@/components/blockButtons'
import DataTable from '@/components/dataTable'
import permissions from '@/mixins/permissions'

export default {
  name: 'CategoryV2',
  components: {
    Folder,
    BlockButtons,
    DataTable
  },
  mixins: [permissions],
  data () {
    return {
      loading: false,
      error: ''
    }
  },
  computed: {
    ...mapGetters('category', ['getData']),
    ...mapGetters('settings', ['getTableColumns']),
    toolbarActions () {
      const actions = [
        {
          id: 'refresh',
          label: 'Обновить',
          icon: RefreshCw,
          disabled: this.loading,
          handler: () => this.loadDataWrapper(true)
        }
      ]

      if (this.isAllowedOperation('create', 'category')) {
        actions.push({
          id: 'create',
          label: 'Новая категория',
          icon: Plus,
          primary: true,
          disabled: this.loading,
          handler: () => {
            this.openModal({
              name: 'create-base',
              propsData: {
                name: 'category',
                moduleAction: 'category',
                type: 'create',
                title: 'Добавление новой категории',
                text: 'Укажите данные категории',
                textCancelBtn: 'Закрыть',
                textConfirmBtn: 'Добавить категорию'
              }
            })
          }
        })
      }

      return actions
    },
    tableColumns () {
      return [
        {
          key: 'actions',
          label: 'Действия',
          sortable: false
        },
        ...(this.getTableColumns?.['category'] || [])
      ]
    },
    rowActions () {
      return [
        {
          id: 'info',
          label: 'Информация',
          visible: true
        },
        {
          id: 'change',
          label: 'Редактировать',
          visible: this.isAllowedOperation('change', 'category')
        },
        {
          id: 'remove',
          label: 'Удалить',
          visible: this.isAllowedOperation('remove', 'category')
        }
      ].filter(({ visible }) => visible)
    }
  },
  mounted () {
    this.loadDataWrapper()
  },
  methods: {
    ...mapActions('category', ['loadData', 'remove']),
    ...mapActions('modal', ['openModal']),
    async loadDataWrapper (force = false) {
      if (this.loading) {
        return
      }

      this.$bus.emit('start-loader')
      this.loading = true

      await this.loadData({ force })

      this.loading = false
      this.$bus.emit('stop-loader')
    },
    selectAction (event, row) {
      const action = event.target.value

      event.target.value = ''

      if (!action) {
        return
      }

      this.openAction(action, row)
    },
    openAction (action, row) {
      switch (action) {
        case 'info':
          this.openModal({
            name: 'info-base',
            propsData: {
              name: 'category',
              moduleAction: 'category',
              type: 'info',
              title: `Информация - ${row.title}`,
              textCancelBtn: 'Закрыть',
              data: row
            }
          })

          break
        case 'change':
          this.openModal({
            name: 'change-base',
            propsData: {
              name: 'category',
              moduleAction: 'category',
              type: 'change',
              title: `Редактирование - ${row.title}`,
              text: 'Измените необходимые данные',
              textCancelBtn: 'Закрыть',
              textConfirmBtn: 'Сохранить',
              data: row
            }
          })

          break
        case 'remove':
          this.removeCategory(row)
      }
    },
    async removeCategory ({ id }) {
      const { error } = await this.remove(id)

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Ошибка при удалении!',
          text: 'Произошла ошибка при удалении категории!',
          clean: false,
          duration: 3000
        })

        return
      }

      this.$notify({
        type: 'success',
        title: 'Категория успешно удалена!',
        text: '',
        clean: false,
        duration: 3000
      })
    }
  }
}
</script>

<style scoped lang="scss" src="./style.scss" />
